/**
 * Hero Icon Components
 *
 * Icon components for Tonnie heroes using Lucide React.
 * Provides type-safe access to hero icons with consistent styling.
 */

import {
  BadgeCheck,
  ClipboardCheck,
  GraduationCap,
  Heart,
  LucideIcon,
  LucideProps,
  Plane,
  Sparkles,
} from 'lucide-react';
import type { HeroType } from '../types';

/**
 * Mapping of hero types to their Lucide icon components
 */
export const HERO_ICON_COMPONENTS: Record<HeroType, LucideIcon> = {
  checkup: ClipboardCheck,
  teleportal: Plane,
  qultureup: GraduationCap,
  care: Heart,
  passport: BadgeCheck,
} as const;

/**
 * Mapping of hero types to their icon names (for reference)
 */
export const HERO_ICON_NAMES: Record<HeroType, string> = {
  checkup: 'ClipboardCheck',
  teleportal: 'Plane',
  qultureup: 'GraduationCap',
  care: 'Heart',
  passport: 'BadgeCheck',
} as const;

/**
 * CoPilot icon component
 */
export const CoPilotIconComponent = Sparkles;

/**
 * Props for the HeroIcon component
 */
export interface HeroIconProps extends LucideProps {
  /** The hero type to display the icon for */
  hero: HeroType;
}

/**
 * HeroIcon Component
 *
 * Renders the appropriate Lucide icon for a given hero type.
 *
 * @example
 * ```tsx
 * <HeroIcon hero="checkup" className="w-6 h-6 text-tone-blue" />
 * <HeroIcon hero="teleportal" size={24} />
 * ```
 */
export function HeroIcon({ hero, ...props }: HeroIconProps) {
  const Icon = HERO_ICON_COMPONENTS[hero];
  return <Icon {...props} />;
}

/**
 * Get the icon component for a specific hero
 */
export function getHeroIconComponent(hero: HeroType): LucideIcon {
  return HERO_ICON_COMPONENTS[hero];
}

/**
 * Get the icon name for a specific hero
 */
export function getHeroIconName(hero: HeroType): string {
  return HERO_ICON_NAMES[hero];
}
